/**
 * API client utilities with org context and Clerk authentication.
 *
 * AC-010: X-Org-Context header sent with all API requests.
 * AC-011: Authorization header with Clerk JWT for authenticated requests.
 */

import { fetchWithBackoff, retryAfterSeconds } from "./rate-limit-backoff";

const ORG_STORAGE_KEY = "command.current-org";
const ROLE_STORAGE_KEY = "command.current-role";
const PROJECT_STORAGE_KEY = "synapsedx-last-project-id";

// Token getter - set by ClerkProvider or auth context
type AuthTokenGetter = (options?: { forceRefresh?: boolean }) => Promise<string | null>;

let getAuthToken: AuthTokenGetter | null = null;
let warnedMissingAuthToken = false;
let lastFreshAuthTokenAtMs = 0;
const AUTH_TOKEN_REFRESH_MAX_AGE_MS = 45 * 60_000;

async function resolveAuthToken(forceRefresh = false): Promise<string | null> {
  let token: string | null = null;
  if (getAuthToken) {
    try {
      token = await getAuthToken({ forceRefresh });
      if (token) {
        if (forceRefresh || lastFreshAuthTokenAtMs <= 0) {
          lastFreshAuthTokenAtMs = Date.now();
        }
        return token;
      }
    } catch (err) {
      console.warn("[API] Failed to resolve auth token via Clerk hook:", err);
    }
  }

  if (typeof window !== "undefined") {
    const clerk = (
      window as Window & {
        Clerk?: {
          session?: { getToken?: (options?: unknown) => Promise<string | null> };
          getToken?: (options?: unknown) => Promise<string | null>;
        };
      }
    ).Clerk;

    try {
      const options = forceRefresh ? { skipCache: true } : undefined;
      if (clerk?.session?.getToken) {
        token = await clerk.session.getToken(options);
      }
      if (!token && clerk?.getToken) {
        token = await clerk.getToken(options);
      }
      if (token) {
        if (forceRefresh || lastFreshAuthTokenAtMs <= 0) {
          lastFreshAuthTokenAtMs = Date.now();
        }
        return token;
      }
    } catch (err) {
      console.warn("[API] Failed to resolve auth token via Clerk global:", err);
    }
  }

  return null;
}

/**
 * Set the auth token getter function.
 * Called by ClerkProvider to inject the token getter.
 */
export function setAuthTokenGetter(getter: AuthTokenGetter): void {
  getAuthToken = getter;
}

/**
 * Get current org ID from localStorage.
 */
function getCurrentOrgId(): string {
  try {
    return localStorage.getItem(ORG_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

/**
 * Get current role ID from localStorage.
 * Returns null for "All Roles" view.
 */
function getCurrentRoleId(): string | null {
  try {
    const value = localStorage.getItem(ROLE_STORAGE_KEY);
    return value && value !== "null" ? value : null;
  } catch {
    return null;
  }
}

function getCurrentProjectId(): string | null {
  try {
    const value = localStorage.getItem(PROJECT_STORAGE_KEY);
    return value && value !== "null" ? value : null;
  } catch {
    return null;
  }
}

/**
 * Create headers with org and role context.
 */
export function createApiHeaders(additionalHeaders?: Record<string, string>): Headers {
  const headers = new Headers(additionalHeaders);
  const orgId = getCurrentOrgId();
  if (orgId) {
    headers.set("X-Org-Context", orgId);
  }
  const roleId = getCurrentRoleId();
  if (roleId) {
    headers.set("X-Role-Context", roleId);
  }
  const projectId = getCurrentProjectId();
  if (projectId) {
    headers.set("X-Project-Context", projectId);
  }
  return headers;
}

/**
 * Fetch with org context, role context, and auth headers.
 */
export async function fetchWithAuth(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  const method = (init?.method ?? "GET").toUpperCase();

  // Add org context
  const orgId = getCurrentOrgId();
  if (orgId && !headers.has("X-Org-Context")) {
    headers.set("X-Org-Context", orgId);
  }

  // Add role context (for tiered data filtering)
  const roleId = getCurrentRoleId();
  if (roleId && !headers.has("X-Role-Context")) {
    headers.set("X-Role-Context", roleId);
  }

  const projectId = getCurrentProjectId();
  if (projectId && !headers.has("X-Project-Context")) {
    headers.set("X-Project-Context", projectId);
  }

  // Add auth token
  if (!headers.has("Authorization")) {
    let token = await resolveAuthToken();
    const tokenStale =
      lastFreshAuthTokenAtMs <= 0 ||
      Date.now() - lastFreshAuthTokenAtMs > AUTH_TOKEN_REFRESH_MAX_AGE_MS;
    if (!token || (!["OPTIONS"].includes(method) && tokenStale)) {
      // Mutating calls and stale-token windows should attempt a fresh token before failing auth.
      token = await resolveAuthToken(true);
    }
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    } else if (!warnedMissingAuthToken) {
      warnedMissingAuthToken = true;
      console.warn("[API] Missing auth token; API requests will return 401.");
    }
  }

  // SYN-3367: honour 429 + Retry-After, so a re-render loop cannot keep re-asking.
  return fetchWithBackoff(input, {
    ...init,
    headers,
  });
}

/**
 * Legacy fetch - only org context, no auth.
 * @deprecated Use fetchWithAuth for authenticated requests.
 */
export async function fetchWithOrgContext(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  return fetchWithAuth(input, init);
}

/**
 * JSON API client with org context and authentication.
 */

async function parseJsonResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") ?? "";
  const text = await response.text();

  if (!contentType.toLowerCase().includes("application/json")) {
    const snippet = text.replace(/\s+/g, " ").trim().slice(0, 160);
    throw new Error(
      `API error: expected JSON but got ${contentType || "unknown content-type"} (${response.status} ${response.statusText})${snippet ? `: ${snippet}` : ""}`,
    );
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    const snippet = text.replace(/\s+/g, " ").trim().slice(0, 160);
    throw new Error(
      `API error: invalid JSON (${response.status} ${response.statusText})${snippet ? `: ${snippet}` : ""}`,
    );
  }
}

/**
 * Thrown for any non-2xx API response. Carries the HTTP status and the
 * parsed JSON body (when the response was valid JSON) alongside the
 * formatted `.message` string, so callers that need to branch on a typed
 * failure code (e.g. the chat connection modal — SYN-2161) don't have to
 * re-parse it out of the message text.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  /** SYN-3367: on a 429, how many seconds the server asked us to wait. */
  readonly retryAfterSeconds: number | undefined;

  constructor(message: string, status: number, body: unknown, retryAfter?: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    const fromBody =
      status === 429 && body && typeof body === "object"
        ? (body as { retryAfter?: unknown }).retryAfter
        : undefined;
    this.retryAfterSeconds =
      retryAfter ??
      (typeof fromBody === "number" && fromBody > 0 ? Math.ceil(fromBody) : undefined);
  }
}

async function buildApiError(response: Response): Promise<ApiError> {
  const contentType = response.headers.get("content-type") ?? "";
  const raw = await response.text();
  let detail = "";
  let parsedBody: unknown = null;

  if (raw) {
    if (contentType.toLowerCase().includes("application/json")) {
      try {
        parsedBody = JSON.parse(raw) as unknown;
        const parsedRecord =
          parsedBody && typeof parsedBody === "object"
            ? (parsedBody as Record<string, unknown>)
            : {};
        const parsedError =
          parsedRecord.error && typeof parsedRecord.error === "object"
            ? (parsedRecord.error as Record<string, unknown>)
            : undefined;
        const message =
          typeof parsedError?.message === "string"
            ? parsedError.message
            : typeof parsedRecord.message === "string"
              ? parsedRecord.message
              : "";
        const code = typeof parsedError?.code === "string" ? parsedError.code : "";
        if (code && message) {
          detail = `${code}: ${message}`;
        } else if (message) {
          detail = message;
        }
      } catch {
        parsedBody = null;
        // Fall back to text snippet below.
      }
    }
    if (!detail) {
      detail = raw.replace(/\s+/g, " ").trim().slice(0, 160);
    }
  }

  return new ApiError(
    `API error: ${response.status} ${response.statusText}${detail ? `: ${detail}` : ""}`,
    response.status,
    parsedBody,
    retryAfterSeconds(response),
  );
}

async function requestWithAuthRetry(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  let response = await fetchWithAuth(input, init);
  if (response.status !== 401) {
    return response;
  }

  // Force-refresh the Clerk JWT and retry once. Without forceRefresh the
  // retry would reuse the same cached (near-expiry/expired) token.
  const freshToken = await resolveAuthToken(true);
  if (!freshToken) {
    return response;
  }
  const retryHeaders = new Headers(init?.headers);
  retryHeaders.set("Authorization", `Bearer ${freshToken}`);
  return fetchWithAuth(input, { ...init, headers: retryHeaders });
}

export const api = {
  /**
   * GET request.
   */
  async get<T>(url: string): Promise<T> {
    const response = await requestWithAuthRetry(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });
    if (!response.ok) {
      throw await buildApiError(response);
    }
    return await parseJsonResponse<T>(response);
  },

  /**
   * POST request.
   */
  async post<T>(url: string, body?: unknown): Promise<T> {
    const response = await requestWithAuthRetry(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) {
      throw await buildApiError(response);
    }
    return await parseJsonResponse<T>(response);
  },

  /**
   * POST with a session token minted for THIS request (SYN-3154).
   *
   * For an answer only the person may give, such as Confirm on a held write. The server refuses
   * any token that was ever handed to the assistant's shell, and the token cached for ordinary
   * calls is the same one the chat request carried there. Minting at the click is what makes the
   * request provably the browser's. Never retried with another token.
   */
  async postWithFreshSession<T>(url: string, body?: unknown): Promise<T> {
    const token = await resolveAuthToken(true);
    if (!token) {
      throw new ApiError("API error: 401 no fresh session token", 401, {
        error: "confirm_in_the_browser",
      });
    }
    const response = await fetchWithAuth(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) {
      throw await buildApiError(response);
    }
    return await parseJsonResponse<T>(response);
  },

  /**
   * PUT request.
   */
  async put<T>(url: string, body?: unknown): Promise<T> {
    const response = await requestWithAuthRetry(url, {
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) {
      throw await buildApiError(response);
    }
    return await parseJsonResponse<T>(response);
  },

  /**
   * PATCH request.
   */
  async patch<T>(url: string, body?: unknown): Promise<T> {
    const response = await requestWithAuthRetry(url, {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) {
      throw await buildApiError(response);
    }
    return await parseJsonResponse<T>(response);
  },

  /**
   * DELETE request. Accepts an optional JSON body for write-with-evidence
   * routes (e.g. artifact-binding unbind, SYN-1742).
   */
  async delete<T>(url: string, body?: unknown): Promise<T> {
    const response = await requestWithAuthRetry(url, {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) {
      throw await buildApiError(response);
    }
    return await parseJsonResponse<T>(response);
  },

  /**
   * Upload a document (base64 JSON body).
   */
  async upload<T>(url: string, body: unknown): Promise<T> {
    const response = await requestWithAuthRetry(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      throw await buildApiError(response);
    }
    return await parseJsonResponse<T>(response);
  },

  /**
   * Download a binary blob (for raw file downloads).
   */
  async downloadBlob(url: string): Promise<Blob> {
    const response = await requestWithAuthRetry(url, { method: "GET" });
    if (!response.ok) {
      throw await buildApiError(response);
    }
    return response.blob();
  },
};
